import React from 'react';
import { Link } from 'react-router-dom';
import { useAccounts } from '../context/AccountContext';

// Top-of-dashboard banner telling a trader/copier what is standing between
// them and trading. It always shows the ACTIONABLE state, in this order:
//
//   1. No MT5 account connected → connect one. This wins even while the user
//      is still `pending`, because MT5 credentials are optional at signup:
//      such a user has nothing for an admin to verify, and "awaiting
//      verification" would leave them waiting for something that is never
//      coming. Connecting an account is what actually moves them forward.
//   2. Accounts exist but the user is `pending` → an admin has to verify.
//   3. Otherwise nothing to say.
//
// Admins are excluded throughout: they never host their own MT5, but they can
// browse these layouts directly or while impersonating.

function Banner({ title, children }) {
  return (
    <div className="verification-banner" role="status" aria-live="polite">
      <div className="verification-banner-inner">
        <span className="verification-banner-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          </svg>
        </span>
        <div className="verification-banner-text">
          <strong>{title}</strong>
          <span>{children}</span>
        </div>
      </div>
    </div>
  );
}

export default function VerificationBanner({ user }) {
  const { accounts, loading } = useAccounts();

  const tradingRole = user?.role === 'trader' || user?.role === 'copier';
  if (!user || !tradingRole) return null;

  // Say nothing until the account list is known. Both messages below hinge on
  // whether the user has an account, so rendering early would flash the wrong
  // one — a `pending` user with none would be told to wait for an admin for a
  // beat before being told to connect an account.
  if (loading) return null;

  if (accounts.length === 0) {
    return (
      <Banner title="Connect an MT5 account to get started">
        {user.role === 'trader'
          ? 'You haven’t connected an MT5 account yet, so there’s nothing to broadcast. '
          : 'You haven’t connected an MT5 account yet, so there’s nothing to copy into. '}
        <Link to={`/${user.role}/accounts`}>Connect one now</Link>
        {' — an admin verifies it, then your MT5 terminal starts.'}
      </Banner>
    );
  }

  if (user.status === 'pending') {
    return (
      <Banner title="Awaiting Admin Verification">
        Your account is pending review. Your MT5 terminal starts — and trading or
        copying unlocks — once an admin verifies you.
      </Banner>
    );
  }

  return null;
}
